import { configureStore } from "@reduxjs/toolkit";
import acting from "./actingSlice";

/**
 * The application store.
 *
 * ONE SLICE, DELIBERATELY. This is the first Redux in the codebase — the rest
 * of the app uses React context (AuthContext) and, in one place, localStorage
 * plus window events (roleOverride.ts). Nothing else is being moved here.
 *
 * Redux earns its place for this specific problem because the acting context is
 * read by the API client, the shell, the navigation and individual screens, and
 * has to survive a reload. A context provider would work; the store gives us
 * a value the non-React API client can read synchronously without a hook, which
 * is exactly what `client.ts` needs to set its headers.
 *
 * If more state moves here later, that should be a decision taken on its own
 * merits rather than by drift.
 */
export const store = configureStore({
  reducer: { acting },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/**
 * Read the acting context OUTSIDE React.
 *
 * `client.ts` sets the two headers on every request and is not a component, so
 * it cannot use a hook. Reading the store directly is the point of having one.
 *
 * Returns null until hydration completes, so no request goes out claiming a
 * context we have not yet confirmed with the server.
 */
export function currentActingContext(): { companyId: string | null; tier: string } | null {
  const { companyId, tier, hydrated } = store.getState().acting;
  /* companyId is legitimately null for a person-tier (an operative), so only
     the tier and hydration are required. Sending a tier without a company is
     exactly how an operative acts, and the server expects it. */
  if (!hydrated || !tier) return null;
  return { companyId, tier };
}
